import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Landmark, Package, Users, Building2, RefreshCw } from '../lib/icons';
import {
  fetchKpis,
  fetchReceptions,
  decompte,
  familleDe,
  labelQualite,
  labelMode,
  nomTiers,
} from '../lib/dataProvider';
import { fmt, StatutBadge, QualiteBadge, ParcoursBadge } from '../components/shared/badges';
import { couleurGain } from '../lib/couleurs';
import { fetchPerformanceFoss } from '../lib/dataProvider';
import { PeriodeFiltre, dansPeriode } from '../components/shared/PeriodeFiltre';
import { CoursDuJour } from '../components/dashboard/CoursDuJour';
import { ComparatifCampagnes } from '../components/dashboard/ComparatifCampagnes';
import { useAuth } from '../lib/auth';
import { getBranding } from '../lib/branding';

import WidgetGraphique from '../components/shared/WidgetGraphique';
import { fetchOperationsCoursier, fetchDepenses, fetchFinances } from '../lib/dataProvider';
import { fetchCategoriesDepenses, EVENEMENT_CATEGORIES } from '../lib/categoriesDepenses';
import { fetchChargesIndustrielles, campagneCourante } from '../lib/cri';
import { totauxChargesCri } from '../lib/criCalculs';
import { suite, dateExercice } from '../lib/financeSuite';
import { peut, voitMontants } from '../lib/permissions';
import { etatHydratation, surHydratation } from '../lib/store';

// Agrégations de présentation uniquement. Les montants et écarts viennent des sources ERP.
const valeurConnue = v => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const montantConnu = v => {
  if (!valeurConnue(v)) throw new Error('Montant source indisponible : graphique non calculé.');
  return Number(v);
};
export function regrouperDepenses(operations, factures, categories, du = '', au = '', finances = null) {
  const libelles = new Map(categories.map(c => [c.valeur, c.libelle]));
  const frais = operations.filter(o => o.typeOperation === 'frais' && o.statut === 'Validée');
  // Une facture est comptée à son TTC, pas une seconde fois à chaque paiement.
  const factureIds = new Set(factures.map(d => d.id).filter(Boolean));
  const fraisIds = new Set(frais.map(o => o.id).filter(Boolean));
  const imputationsFrais = new Set(frais.map(o => o.refImputation).filter(Boolean));
  const imputationsFactures = new Set(factures.flatMap(d => (d.reglements ?? [])
    .flatMap(r => [r.refImputation, r.mouvementId, r.reglementFinanceId])).filter(Boolean));
  const estLiee = r => factureIds.has(r.depenseId) || factureIds.has(r.factureId)
    || imputationsFactures.has(r.id) || fraisIds.has(r.piece) || imputationsFrais.has(r.id);
  // Anciennes factures : leur mouvement n'a pas de depenseId. Reconnaissance
  // du libellé EXACT produit par reglerDepense/marquerDecaisse + tous les champs
  // du règlement enfant. Jamais un rapprochement par montant/date seuls.
  const paiementFactureHistorique = m => factures.some(d => (d.reglements ?? []).some(r => {
    if (r.differe && !r.decaisse) return false;
    const noms = [
      `${d.categorie} — facture ${d.numeroFacture} (${d.fournisseur})`,
      `${r.mode} ${r.piece} — facture ${d.numeroFacture} (${d.fournisseur})`,
    ];
    return m.compteId === r.compteId && m.date === (r.decaisseLe || r.date)
      && String(m.piece ?? '') === String(r.piece || d.numeroFacture || '')
      && valeurConnue(m.montant) && valeurConnue(r.montant)
      && Number(m.montant) === Number(r.montant) && noms.includes(m.libelle);
  }));
  if (finances !== null && (!Array.isArray(finances?.mouvements) || !Array.isArray(finances?.reglements))) {
    throw new Error('Registre financier indisponible : graphique non calculé.');
  }
  const financiers = [
    ...(finances?.mouvements ?? []).filter(m => m.type === 'Dépense' && !m.deleted && !m.supprime
      && !m.acompte && !estLiee(m) && !paiementFactureHistorique(m)).map(m => ({ ...m, registre: 'mouvement' })),
    ...(finances?.reglements ?? []).filter(r => r.propositionId && r.statut === 'Payé'
      && !r.acompte && !r.deleted && !r.supprime && !estLiee(r)).map(r => ({ ...r, registre: 'reglement' })),
  ];
  const vus = new Map();
  const depensesFinancieres = financiers.filter(r => {
    const cle = r.cle_effet || (r.id ? `${r.registre}:${r.id}` : null);
    if (!cle) throw new Error('Identifiant financier manquant : graphique non calculé.');
    const signature = JSON.stringify([r.date, r.categorie, r.montant, r.compteId]);
    if (vus.has(cle)) {
      if (vus.get(cle) !== signature) throw new Error('Écritures financières contradictoires : vérification requise.');
      return false;
    }
    vus.set(cle, signature);
    return true;
  });
  const lignes = [
    ...frais.map(o => ({ date: o.date, categorie: o.categorie, montant: o.montant })),
    ...factures.map(d => ({ date: d.date, categorie: d.categorie, montant: d.montantTTC })),
    ...depensesFinancieres,
  ].filter(d => dansPeriode(d.date, du, au));
  const groupes = lignes.reduce((m, d) => {
    const valeur = String(d.categorie ?? '').trim();
    const label = valeur ? (libelles.get(valeur) || valeur) : 'Non catégorisé';
    m.set(label, (m.get(label) ?? 0) + montantConnu(d.montant));
    return m;
  }, new Map());
  return [...groupes].map(([label, valeur]) => ({ label, valeur: Number(valeur.toFixed(3)) }))
    .sort((a, b) => b.valeur - a.valeur || a.label.localeCompare(b.label, 'fr'));
}
export function classerFournisseurs(details) {
  const groupes = details.reduce((m, d) => {
    const label = String(d.tiers || 'Fournisseur non renseigné');
    // ecart est déjà pondéré par les kg et calculé sans l'arrondi d'affichage de reel.
    m.set(label, (m.get(label) ?? 0) + montantConnu(d.ecart));
    return m;
  }, new Map());
  return [...groupes].map(([label, valeur]) => ({ label, valeur: Number(valeur.toFixed(3)) }))
    .sort((a, b) => b.valeur - a.valeur || a.label.localeCompare(b.label, 'fr')).slice(0, 10);
}
export function rendementsParDate(details) {
  const groupes = details.reduce((m, d) => {
    if (!d.date) throw new Error('Date source manquante : rendements non regroupés.');
    const date = String(d.date).slice(0, 10);
    const g = m.get(date) ?? { date, reel: 0, foss: 0, n: 0 };
    g.reel += montantConnu(d.reel); g.foss += montantConnu(d.foss); g.n += 1;
    m.set(date, g); return m;
  }, new Map());
  // Moyennes simples des lots par jour, expressément demandées ; pas un rendement industriel pondéré.
  return [...groupes.values()].sort((a, b) => a.date.localeCompare(b.date))
    .map(g => ({ date: g.date, serie1: g.reel / g.n, serie2: g.foss / g.n }));
}
export function courbeTresorerie(rapport) {
  const central = rapport?.tresorerie?.find(s => s.scenario?.id === 'central');
  if (!central || !Array.isArray(central.semaines)) throw new Error('Scénario central indisponible dans le rapport de pilotage.');
  // Conversion d'unité seulement. La prévision et son ordre sont intégralement ceux du serveur.
  return central.semaines.map(w => ({ date: w.au,
    valeur: w.solde_fin_millimes == null ? null : montantConnu(w.solde_fin_millimes) / 1000 }));
}
const presenter = fn => { try { return { donnees: fn(), erreur: '' }; } catch (e) { return { donnees: [], erreur: e.message }; } };

const Widget = ({ testid, titre, valeur, unite, lignes, accent, footer }) => (
  <div data-testid={testid} className={`animate-fade-up border border-slate-200 border-l-2 bg-white ${accent}`}>
    <div className="px-4 pt-4">
      <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{titre}</p>
      <p className="tabular mt-2 font-data text-3xl font-medium tracking-tight text-slate-900">
        {valeur} <span className="text-sm text-slate-500">{unite}</span>
      </p>
    </div>
    <dl className="mt-3 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
      {lignes.map(([k, v, testId, ton]) => (
        <div key={k} className="bg-white px-3 py-2">
          <dt className="text-[11px] text-slate-500">{k}</dt>
          <dd
            data-testid={testId}
            className={`tabular font-data text-sm font-semibold ${
              ton === 'lampante' ? 'text-orange-700' : ton === 'clients' ? 'text-sky-700' : 'text-slate-900'
            }`}
          >
            {v}
          </dd>
        </div>
      ))}
    </dl>
    {footer && <p className="border-t border-slate-200 px-4 py-2 font-data text-[11px] text-slate-500">{footer}</p>}
  </div>
);

export default function Dashboard() {
  const [k, setK] = useState(null);
  const [rows, setRows] = useState([]);
  const [globalBanques, setGlobalBanques] = useState(false);
  const [branding, setBrandingState] = useState(getBranding());
  const [perf, setPerf] = useState({ lots: 0, bonus: 0, malus: 0, net: 0, theorique: 0, reel: 0, details: [] });
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const { user } = useAuth();
  const estAdmin = user?.role === 'Administrateur';
  const [hydratation, setHydratation] = useState(etatHydratation);
  const [revision, setRevision] = useState(0);
  const [sources, setSources] = useState({ depenses: { chargement: true }, cri: { chargement: true } });
  const [pilotage, setPilotage] = useState({ chargement: true });
  const [perfErreur, setPerfErreur] = useState('');
  const [perfCharge, setPerfCharge] = useState(true);
  const pretes = hydratation.etat === 'ok';
  const droitDepenses = voitMontants(user?.role) && peut(user?.role, 'depenses') && peut(user?.role, 'frais-generaux');
  const droitFinances = voitMontants(user?.role) && peut(user?.role, 'finances');
  const droitCri = voitMontants(user?.role) && peut(user?.role, 'cri');
  const droitPilotage = voitMontants(user?.role) && peut(user?.role, 'reporting-finance');
  const campagne = campagneCourante();
  const datePilotage = au || dateExercice(Number(new Intl.DateTimeFormat('en', { timeZone: 'Africa/Tunis', year: 'numeric' }).format(new Date())));

  useEffect(() => {
    const stop = surHydratation(setHydratation);
    const maj = () => setRevision(n => n + 1);
    window.addEventListener('store-maj', maj);
    window.addEventListener(EVENEMENT_CATEGORIES, maj);
    return () => { stop(); window.removeEventListener('store-maj', maj); window.removeEventListener(EVENEMENT_CATEGORIES, maj); };
  }, []);

  useEffect(() => {
    let actif = true;
    setSources({ depenses: { chargement: true }, cri: { chargement: true } });
    if (!pretes) return () => { actif = false; };
    const enregistrer = (cle, etat) => { if (actif) setSources(p => ({ ...p, [cle]: etat })); };
    if (droitDepenses) Promise.all([fetchOperationsCoursier(), fetchDepenses(), fetchCategoriesDepenses(), droitFinances ? fetchFinances() : Promise.resolve(null)])
      .then(([operations, factures, categories, finances]) => enregistrer('depenses', presenter(() => regrouperDepenses(operations, factures, categories, du, au, finances))))
      .catch(e => enregistrer('depenses', { erreur: e.message }));
    if (droitCri) fetchChargesIndustrielles({ campagne })
      .then(charges => enregistrer('cri', presenter(() => totauxChargesCri(charges).parCategorie.map(([label, valeur]) => ({ label, valeur })))))
      .catch(e => enregistrer('cri', { erreur: e.message }));
    return () => { actif = false; };
  }, [du, au, campagne, droitDepenses, droitFinances, droitCri, pretes, revision, user?.email]);

  useEffect(() => {
    const c = new AbortController();
    setPilotage({ chargement: true });
    if (droitPilotage) suite('/pilotage?au=' + encodeURIComponent(datePilotage), { signal: c.signal })
      .then(rapport => { if (!c.signal.aborted) setPilotage({ rapport, ...presenter(() => courbeTresorerie(rapport)) }); })
      .catch(e => { if (!c.signal.aborted) setPilotage({ erreur: e.message }); });
    return () => c.abort();
  }, [datePilotage, droitPilotage, user?.email]);

  const lecture = etat => !pretes
    ? { chargement: hydratation.etat === 'en_cours', erreur: hydratation.etat === 'echec' ? (hydratation.erreur || 'Données serveur non chargées.') : '' }
    : etat;
  const classement = presenter(() => classerFournisseurs(perf.details));
  const rendements = presenter(() => rendementsParDate(perf.details));
  const central = pilotage.rapport?.tresorerie?.find(s => s.scenario?.id === 'central');


  useEffect(() => {
    let actif = true;
    setPerfCharge(true); setPerfErreur('');
    if (estAdmin && pretes) fetchPerformanceFoss(du, au)
      .then(data => { if (actif) { setPerf(data); setPerfCharge(false); } })
      .catch(e => { if (actif) { setPerfErreur(e.message); setPerfCharge(false); } });
    fetchKpis(du, au).then(data => { if (actif) setK(data); });
    fetchReceptions().then(rs => { if (actif) setRows(rs.filter(r => dansPeriode(r.date, du, au))); });
    return () => { actif = false; };
  }, [du, au, estAdmin, pretes, revision]);

  useEffect(() => {
    const maj = () => setBrandingState(getBranding());
    window.addEventListener('branding-maj', maj);
    return () => window.removeEventListener('branding-maj', maj);
  }, []);

  if (!k) return null;

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <section
        data-testid="dashboard-banniere"
        className="relative overflow-hidden border border-slate-200"
      >
        <img src={branding.banniere} alt="Oliveraie" className="h-40 w-full object-cover sm:h-48" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/55 to-transparent" />
        <div className="absolute inset-0 flex items-center gap-4 px-5 sm:px-8">
          {branding.logo && (
            <img
              data-testid="banniere-logo"
              src={branding.logo}
              alt="Logo"
              className="hidden h-20 w-20 bg-white/90 object-contain p-1.5 sm:block"
            />
          )}
          <div>
            <p className="font-data text-[11px] uppercase tracking-[0.16em] text-emerald-300">
              Campagne oléicole 2026
            </p>
            <h1 className="font-heading text-2xl font-black tracking-tight text-white sm:text-3xl">
              {branding.nom}
            </h1>
            <p className="mt-1 max-w-xl text-sm text-slate-200">{branding.slogan}</p>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-black tracking-tight text-slate-900">Vue d'ensemble</h2>
          <p className="mt-1 text-sm text-slate-600">
            Ventilation détaillée des flux — filière lampante et comptes tiers cloisonnés{(du || au) && <span data-testid="dashboard-periode-active" className="ml-2 border border-slate-900 px-1.5 font-data text-[11px] font-bold text-slate-900">période {du || '…'} → {au || '…'}</span>}
          </p>
        </div>
        <PeriodeFiltre du={du} au={au} setDu={setDu} setAu={setAu} testid="periode-dashboard" />
      </div>

      <CoursDuJour />
      <ComparatifCampagnes />

      {estAdmin && (
        <section data-testid="widget-performance-foss" className="border border-slate-200 border-l-2 border-l-violet-600 bg-white">
          <div className="flex flex-col gap-1 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-heading text-base font-bold tracking-tight text-slate-900">
                Performance d'Achat (Foss vs Réel)
              </h2>
              <p className="text-xs text-slate-500">
                Écart cumulé de la campagne sur {perf.lots} lot(s) achetés au rendement théorique — vue Administrateur
              </p>
            </div>
            <p
              data-testid="perf-foss-net"
              className={`tabular font-data text-2xl font-medium ${couleurGain(perf.net)}`}
            >
              {perf.net >= 0 ? '+' : '−'}{fmt(Math.abs(perf.net), 3)} <span className="text-sm text-slate-500">Kg</span>
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-px bg-slate-200 md:grid-cols-4">
            {[
              ['Huile théorique', `${fmt(perf.theorique, 3)} Kg`],
              ['Huile réelle', `${fmt(perf.reel, 3)} Kg`],
              ['Bonus cumulé', `+${fmt(perf.bonus, 3)} Kg`],
              ['Malus cumulé', `${fmt(perf.malus, 3)} Kg`],
            ].map(([k, v]) => (
              <div key={k} className="bg-white px-4 py-3">
                <dt className="text-[11px] uppercase tracking-[0.08em] text-slate-500">{k}</dt>
                <dd className="tabular font-data text-sm font-semibold text-slate-900">{v}</dd>
              </div>
            ))}
          </dl>
          {perf.details.length > 0 && (
            <div className="overflow-x-auto border-t border-slate-200">
              <table data-testid="perf-fosse-details" className="w-full text-left">
                <thead className="bg-slate-50">
                  <tr>{['Date', 'Lot', 'Fournisseur', 'Qualité', 'Olives (kg)', 'FOSS %', 'Réel %', 'Huile théo.', 'Huile réelle', 'Écart kg', 'Écart TND', 'Coût / kg', 'OF'].map((c) => <th key={c} className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">{c}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {perf.details.map((d) => (
                    <tr key={d.id} data-testid={`perf-fosse-${d.id}`} className={`text-xs ${d.ecart >= 0 ? 'bg-emerald-50/40' : 'bg-red-50/40'}`}>
                      <td className="px-3 py-1.5 font-data">{d.date}</td><td className="px-3 py-1.5 font-data font-semibold">{d.id}</td><td className="px-3 py-1.5">{d.tiers}</td><td className="px-3 py-1.5">{d.qualite}</td>
                      <td className="px-3 py-1.5 font-data">{fmt(d.olives)}</td><td className="px-3 py-1.5 font-data">{fmt(d.foss, 2)}</td><td className="px-3 py-1.5 font-data font-bold">{fmt(d.reel, 2)}</td>
                      <td className="px-3 py-1.5 font-data">{fmt(d.theorique, 1)}</td><td className="px-3 py-1.5 font-data">{fmt(d.huile, 1)}</td>
                      <td className={`px-3 py-1.5 font-data font-bold ${couleurGain(d.ecart)}`}>{d.ecart >= 0 ? '+' : '−'}{fmt(Math.abs(d.ecart), 1)}</td>
                      <td className={`px-3 py-1.5 font-data font-bold ${couleurGain(d.ecartTnd)}`}>{d.ecartTnd >= 0 ? 'Bonus +' : 'Malus −'}{fmt(Math.abs(d.ecartTnd), 3)}</td>
                      <td className="px-3 py-1.5 font-data">{d.coutKg != null ? fmt(d.coutKg, 3) : '—'}</td>
                      <td className="px-3 py-1.5 font-data">{d.of ?? '—'}{d.mixe && <span className="ml-1 border border-amber-300 bg-amber-50 px-1 text-[9px] font-bold text-amber-700">MIXÉ</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Widget
          testid="widget-tonnage"
          accent="border-l-slate-900"
          titre="Tonnage du Jour (Kg)"
          valeur={fmt(k.tonnage.total)}
          unite="Kg"
          lignes={[
            ['En attente de traitement', fmt(k.tonnage.attente), 'tonnage-attente'],
            ['Déjà traité', fmt(k.tonnage.traite), 'tonnage-traite'],
            ['Trituration (prestation client)', fmt(k.tonnage.prestation), 'tonnage-prestation'],
            ['Achat (fournisseur)', fmt(k.tonnage.achat), 'tonnage-achat'],
            ['Extra-Vierge / Vierge', fmt(k.tonnage.noble), 'tonnage-noble'],
            ['Lampante', fmt(k.tonnage.lampante), 'tonnage-lampante', 'lampante'],
          ]}
        />

        <Widget
          testid="widget-rendements"
          accent="border-l-emerald-600"
          titre="Rendements par filière (%)"
          valeur={fmt(k.rendements.extra, 2)}
          unite="% nobles"
          lignes={[
            ['Rendement Extra-Vierge / Vierge', `${fmt(k.rendements.extra, 2)} %`, 'rendement-extra'],
            ['Rendement Lampante', `${fmt(k.rendements.lampante, 2)} %`, 'rendement-lampante', 'lampante'],
            ['Rendement Trituration (client)', `${fmt(k.rendements.trituration, 2)} %`, 'rendement-trituration', 'clients'],
            ['Base de calcul', 'lots avec rendement connu', 'rendement-base'],
          ]}
        />

        <Widget
          testid="widget-cuves"
          accent="border-l-sky-600"
          titre="Volume en Cuves (Kg)"
          valeur={fmt(k.volumes.total)}
          unite="kg"
          lignes={[
            ['Propriété de l’huilerie', fmt(k.volumes.huilerie), 'cuves-huilerie'],
            ['Compte clients / agriculteurs', fmt(k.volumes.clients), 'cuves-clients', 'clients'],
            ['Filière nobles', fmt(k.volumes.noble), 'cuves-noble'],
            ['Filière lampante', fmt(k.volumes.lampante), 'cuves-lampante', 'lampante'],
          ]}
          footer="Les huiles stockées pour le compte des tiers ne sont pas la propriété de l'huilerie."
        />
      </div>

      <section data-testid="widget-banques" className="border border-slate-200 border-l-2 border-l-slate-900 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <Landmark size={16} /> Trésorerie multi-banques
            </h2>
            <p className="text-xs text-slate-500">
              Comptes Attijari et Biatnet — prêt pour la synchronisation des relevés
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="toggle-banques"
              onClick={() => setGlobalBanques((v) => !v)}
              className="h-11 border border-slate-300 px-3 text-xs font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-auto sm:py-2"
            >
              {globalBanques ? 'Voir par banque' : 'Voir le solde global'}
            </button>
            <button
              type="button"
              data-testid="sync-banques"
              onClick={() => fetchKpis(du, au).then(setK)}
              className="flex h-11 items-center gap-1.5 border border-slate-300 px-3 text-xs font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-auto sm:py-2"
            >
              <RefreshCw size={13} /> Synchroniser
            </button>
          </div>
        </div>

        {globalBanques ? (
          <div data-testid="banques-global" className="grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-3">
            {[
              ['Solde bancaire global (théorique)', fmt(k.tresorerie.banques, 2), 'banques-total'],
              ['Solde bancaire rapproché (pointé)', fmt(k.tresorerie.banquesPointe, 2), 'banques-pointe-total'],
              ['Coffre-fort + Caisse bascule', fmt(k.tresorerie.caisses, 2), 'caisses-total'],
              ['Trésorerie totale', fmt(k.tresorerie.banques + k.tresorerie.caisses, 2), 'tresorerie-total'],
            ].map(([kk, v, id]) => (
              <div key={kk} className="bg-white px-4 py-4">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">{kk}</p>
                <p data-testid={id} className="tabular mt-2 font-data text-2xl font-medium text-slate-900">
                  {v} <span className="text-sm text-slate-500">TND</span>
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div data-testid="banques-detail" className="grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-3">
            {k.banques.map((b) => (
              <div key={b.id} className="bg-white px-4 py-4">
                <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                  <Building2 size={13} /> {b.nom}
                </p>
                <p data-testid={`banque-solde-${b.id}`} className="tabular mt-2 font-data text-2xl font-medium text-slate-900">
                  {fmt(b.solde, 2)} <span className="text-sm text-slate-500">TND</span>
                </p>
                <p className="mt-1 font-data text-[11px] text-slate-500">{b.rib}</p>
                <p data-testid={`banque-pointe-${b.id}`} className="mt-1 font-data text-[11px] text-emerald-700">
                  Rapproché (pointé) : {fmt(b.soldePointe, 2)} TND
                </p>
                <p className="mt-1 font-data text-[10px] uppercase tracking-[0.1em] text-slate-400">
                  Écart à rapprocher : {fmt(b.solde - b.soldePointe, 2)} TND
                </p>
              </div>
            ))}
            <div className="bg-white px-4 py-4">
              <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Caisses internes</p>
              {k.caisses.map((c) => (
                <p key={c.id} data-testid={`caisse-solde-${c.id}`} className="tabular mt-1 font-data text-sm text-slate-900">
                  {c.nom} : {fmt(c.solde, 2)} TND
                </p>
              ))}
            </div>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div
          data-testid="lampante-summary"
          className="flex flex-col gap-3 border border-slate-200 border-l-2 border-l-orange-500 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-orange-700">
              Filière Lampante — hors bilan nobles
            </p>
            <p className="text-xs text-slate-500">Comptabilité et stockage cloisonnés</p>
          </div>
          <div className="flex gap-6">
            <div>
              <p className="text-[11px] text-slate-500">Tonnage (Kg)</p>
              <p data-testid="lampante-tonnage" className="tabular font-data text-xl font-semibold text-slate-900">
                {fmt(k.lampante.tonnage)}
              </p>
            </div>
            <div>
              <p className="text-[11px] text-slate-500">Cuves (Kg)</p>
              <p data-testid="lampante-volume" className="tabular font-data text-xl font-semibold text-slate-900">
                {fmt(k.lampante.volume)}
              </p>
            </div>
          </div>
        </div>

        <div
          data-testid="comptoir-summary"
          className="flex flex-col gap-3 border border-slate-200 border-l-2 border-l-sky-600 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-sky-700">
              <Package size={13} /> Comptoir emballages
            </p>
            <p className="text-xs text-slate-500">Ventes réalisées lors des sorties clients</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500">Chiffre d'affaires (TND)</p>
            <p data-testid="comptoir-total" className="tabular font-data text-xl font-semibold text-slate-900">
              {fmt(k.comptoir, 2)}
            </p>
          </div>
        </div>
      </div>

      {(estAdmin || droitDepenses || droitCri || droitPilotage) && <section data-testid="dashboard-analyses" className="space-y-4">
        <h2 className="font-heading text-xl font-bold text-slate-900">Analyses</h2>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {droitDepenses && <WidgetGraphique type="camembert" titre="Dépenses par catégorie" unite="TND" testid="graph-depenses"
            description={droitFinances ? "Factures TTC, frais coursier validés et dépenses financières supplémentaires, dont frais bancaires et règlements Passerelle payés. Écritures liées comptées une seule fois ; avances et transferts exclus." : "Factures TTC et frais coursier validés sur la période. Les dépenses financières ne sont pas incluses avec les droits de cette session."}
            {...lecture(sources.depenses)} />}
          {estAdmin && <WidgetGraphique type="barres" titre="Classement fournisseurs — bonus/malus cumulé (top 10)" unite="kg" testid="graph-fournisseurs"
            description="Somme des écarts d’huile déjà calculés, tri décroissant. Un total cumulé dépend du volume acheté ; ce n’est pas un taux de performance. Regroupement par nom fourni par la source."
            {...lecture({ ...classement, chargement: perfCharge, erreur: perfErreur || classement.erreur })} />}
          {estAdmin && <WidgetGraphique type="courbe" titre="Rendement réel vs théorique" unite="%" testid="graph-rendements"
            description="Période du tableau de bord — moyennes simples des lots de chaque jour, sans pondération supplémentaire."
            {...lecture({ ...rendements, chargement: perfCharge, erreur: perfErreur || rendements.erreur })}
            series={[{ cle: 'serie1', label: 'Réel' }, { cle: 'serie2', label: 'Théorique' }]} />}
          {droitCri && <WidgetGraphique type="camembert" titre="Charges CRI par catégorie" unite="TND" testid="graph-cri"
            description={'Campagne ' + campagne + ' complète, indépendante du filtre de dates. Charges normales incorporées ; exceptionnelles exclues comme dans le module CRI.'}
            {...lecture(sources.cri)} />}
          {droitPilotage && <div className="min-w-0 space-y-2 xl:col-span-2">
            <WidgetGraphique type="courbe" titre="Trésorerie prévisionnelle — 13 semaines" unite="TND" testid="graph-tresorerie"
              description={'Scénario central — soldes de fin de semaine. Exercice du rapport au ' + datePilotage + (central ? ' ; horizon configuré ' + central.debut + ' → ' + central.fin + '. ' + (central.statut === 'partiel' ? 'PRÉVISION PARTIELLE : hypothèses à revoir.' : 'Hypothèses revues.') : '')}
              {...pilotage} series={[{ cle: 'valeur', label: 'Solde prévisionnel' }]} />
            {pilotage.rapport && <div className="text-xs text-slate-600"><p>{pilotage.rapport.limite_dates}</p>
              {pilotage.rapport.alertes_hypotheses?.map((a, i) => <p key={i} className="text-amber-800">{a}</p>)}
              <Link className="underline" to="/reporting-finance">Consulter les hypothèses, retards et exclusions dans Finance & Comptabilité</Link>
            </div>}
          </div>}
        </div>
      </section>}

      <section data-testid="data-table-section" className="border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <Users size={15} /> Suivi des flux — dernières réceptions
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              {rows.length} enregistrement{rows.length === 1 ? '' : 's'}
            </p>
          </div>
          <Link
            to="/reception"
            data-testid="goto-reception-link"
            className="flex h-11 items-center justify-center border border-slate-300 px-3 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white sm:h-auto sm:py-2"
          >
            Ouvrir le module
          </Link>
        </div>

        <div className="overflow-x-auto">
          <table data-testid="receptions-table" className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date', 'Parcours', 'Tiers', 'Qualité', 'Mode', 'Quantité (Kg)', 'Rdt (%)', 'Statut'].map((c) => (
                  <th key={c} className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr data-testid="table-empty-state">
                  <td colSpan={8} className="px-3 py-14 text-center text-sm text-slate-500">
                    Aucune donnée enregistrée.
                  </td>
                </tr>
              ) : (
                rows.slice(0, 8).map((r) => {
                  const c = decompte(r);
                  const lamp = r.parcours === 'fournisseur' && familleDe(r.qualite) === 'lampante';
                  return (
                    <tr
                      key={r.id}
                      data-testid={`table-row-${r.id}`}
                      className={`border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50 ${
                        lamp ? 'bg-orange-50/40' : ''
                      }`}
                    >
                      <td className="px-3 py-2 font-data text-sm text-slate-800">{r.date}</td>
                      <td className="px-3 py-2"><ParcoursBadge parcours={r.parcours} /></td>
                      <td className="px-3 py-2 text-sm text-slate-800">{nomTiers(r.tiersId)}</td>
                      <td className="px-3 py-2">
                        {r.qualite ? (
                          <QualiteBadge famille={familleDe(r.qualite)} label={labelQualite(r.qualite)} />
                        ) : (
                          <span className="font-data text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-slate-700">{labelMode(r.mode)}</td>
                      <td className="tabular px-3 py-2 font-data text-sm text-slate-900">{fmt(c.poidsNet)}</td>
                      <td className="tabular px-3 py-2 font-data text-sm text-slate-900">
                        {c.enAttenteRendement ? '—' : fmt(c.rendement, 2)}
                      </td>
                      <td className="px-3 py-2"><StatutBadge statut={r.statut} /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
